import * as THREE from "three/webgpu";
import {
  Fn,
  If,
  PI,
  clamp,
  cos,
  instanceIndex,
  mix,
  positionLocal,
  sin,
  smoothstep,
  step,
  storage,
  uniform,
  vec3,
  vec4,
} from "three/tsl";
import type { BattleTeam } from "./BattleInteractionRouter";
import { createToonGradient } from "../toon";
import { TORNADO_DURATION } from "./BattleTornado";
import { FLOOD_DURATION, RIFT_DURATION } from "./BattleCataclysms";
import { CAT_RUSH_DURATION } from "./BattleCatRush";
import { BIPED_DURATION, HOUND_DURATION } from "./BattleRobotSkills";

const GPU_PER_TEAM = 2_048;
const GPU_MAX = GPU_PER_TEAM * 2;
const CPU_PER_TEAM = 512;
const CPU_MAX = CPU_PER_TEAM * 2;
const MATRIX = new THREE.Matrix4();
const POSITION = new THREE.Vector3();
const QUATERNION = new THREE.Quaternion();
const SCALE = new THREE.Vector3();
const EULER = new THREE.Euler();
const TINT = new THREE.Color();
const FUR = new THREE.Color(0xffdbae);
const PILLOW = new THREE.Color(0xffefc7);

export type SwarmFrame = {
  emberVisible: number;
  aquaVisible: number;
  frontline: number;
  reducedMotion: boolean;
};

export type BattleSwarm = {
  readonly gpuSimulation: boolean;
  update(dt: number, time: number, frame: SwarmFrame): void;
  triggerCharge(team: BattleTeam, time: number): void;
  triggerTornado(team: BattleTeam, time: number): void;
  triggerFlood(team: BattleTeam, time: number): void;
  triggerRift(team: BattleTeam, time: number): void;
  triggerCatRush(attacker: BattleTeam, time: number): void;
  triggerBiped(attacker: BattleTeam, time: number): void;
  triggerHound(attacker: BattleTeam, time: number): void;
  snapshot(): { emberVisible: number; aquaVisible: number; capacity: number };
  dispose(): void;
};

export class GpuBattleSwarm implements BattleSwarm {
  readonly gpuSimulation = true;
  readonly #renderer: THREE.WebGPURenderer;
  readonly #meshes: THREE.InstancedMesh[] = [];
  readonly #metaAttribute = new THREE.StorageInstancedBufferAttribute(GPU_MAX, 4);
  readonly #stateAttribute = new THREE.StorageInstancedBufferAttribute(GPU_MAX, 4);
  readonly #colorAttribute = new THREE.StorageInstancedBufferAttribute(GPU_MAX, 4);
  readonly #time = uniform(0);
  readonly #delta = uniform(0.016);
  readonly #frontline = uniform(0);
  readonly #emberCount = uniform(120);
  readonly #aquaCount = uniform(120);
  readonly #motionScale = uniform(1);
  readonly #emberChargeStart = uniform(-10);
  readonly #aquaChargeStart = uniform(-10);
  readonly #tornadoTarget = uniform(0);
  readonly #tornadoStart = uniform(-10);
  readonly #floodTarget = uniform(0);
  readonly #floodStart = uniform(-10);
  readonly #riftTarget = uniform(0);
  readonly #riftStart = uniform(-10);
  readonly #catTarget = uniform(0);
  readonly #catStart = uniform(-10);
  readonly #bipedTarget = uniform(0);
  readonly #bipedStart = uniform(-10);
  readonly #houndTarget = uniform(0);
  readonly #houndStart = uniform(-10);
  readonly #computeNode;
  readonly #toonGradient = createToonGradient();
  #visible: [number, number] = [0, 0];

  constructor(scene: THREE.Scene, renderer: THREE.WebGPURenderer) {
    this.#renderer = renderer;
    this.#initializeAttributes();
    const meta = storage(this.#metaAttribute, "vec4", GPU_MAX);
    const states = storage(this.#stateAttribute, "vec4", GPU_MAX);
    const colors = storage(this.#colorAttribute, "vec4", GPU_MAX);

    this.#computeNode = Fn(() => {
      const info = meta.element(instanceIndex);
      const state = states.element(instanceIndex);
      const side = step(0, info.x);
      const visibleLimit = mix(this.#emberCount, this.#aquaCount, side);
      If(info.y.greaterThanEqual(visibleLimit), () => {
        state.assign(vec4(10_000, 10_000, 10_000, 0));
      }).Else(() => {
        const seed = info.z;
        const rank = info.w;
        const depth = seed.mul(2.65).add(rank.mul(1.25)).add(0.16);
        const chargeStart = mix(this.#emberChargeStart, this.#aquaChargeStart, side);
        const chargeAge = this.#time.sub(chargeStart);
        const chargeGate = step(0, chargeAge).mul(step(chargeAge, 0.78));
        const chargeEnvelope = sin(clamp(chargeAge.div(0.78), 0, 1).mul(PI)).mul(chargeGate).mul(this.#motionScale);
        const chargeTravel = chargeEnvelope.mul(mix(0.48, 0.18, rank));
        const collisionDepth = clamp(depth.sub(chargeTravel), 0.1, 4.2);
        const collisionX = this.#frontline.add(info.x.mul(collisionDepth));
        const laneZ = sin(seed.mul(91.7)).mul(2.65).add(sin(rank.mul(37.1)).mul(0.38));
        const engaged = smoothstep(1.28, 0.12, collisionDepth);
        const impactPulse = sin(this.#time.mul(17).add(seed.mul(83)).add(rank.mul(29))).abs();
        const recoil = impactPulse.mul(0.16).mul(engaged).mul(this.#motionScale);
        const targetX = collisionX.add(info.x.mul(recoil));
        const x = mix(state.x, targetX, clamp(this.#delta.mul(9.5), 0, 1));
        const z = mix(state.z, laneZ, clamp(this.#delta.mul(6), 0, 1));
        const march = sin(this.#time.mul(9.5).add(seed.mul(52))).abs().mul(0.075).add(chargeEnvelope.mul(0.1)).mul(this.#motionScale);
        const clashTilt = sin(this.#time.mul(11).add(seed.mul(19))).mul(0.13).mul(engaged).add(info.x.mul(chargeEnvelope).mul(-0.13));
        state.assign(vec4(x, march, z, clashTilt));

        const stormAge = this.#time.sub(this.#tornadoStart);
        const stormActive = stormAge.greaterThanEqual(0).and(stormAge.lessThan(TORNADO_DURATION));
        const selected = info.x.sub(this.#tornadoTarget).abs().lessThan(0.1).and(seed.lessThan(0.78));
        If(stormActive.and(selected), () => {
          const progress = clamp(stormAge.div(TORNADO_DURATION), 0, 1);
          const suction = smoothstep(0.02, 0.62, progress);
          const release = step(progress, 0.93);
          const angle = seed.mul(57).add(progress.mul(PI).mul(12));
          const centerX = this.#frontline.add(this.#tornadoTarget.mul(1.55));
          const radius = mix(3.25, 0.22, suction);
          state.x.assign(mix(x, centerX.add(cos(angle).mul(radius)), suction.mul(release)));
          state.z.assign(mix(z, sin(angle).mul(radius), suction.mul(release)));
          state.y.addAssign(sin(progress.mul(PI)).mul(2.4).add(suction.mul(seed.mul(5.8).add(1.1))).mul(release));
          state.w.assign(angle);
        });

        const floodAge = this.#time.sub(this.#floodStart);
        const floodActive = floodAge.greaterThanEqual(0).and(floodAge.lessThan(FLOOD_DURATION));
        const floodSelected = info.x.sub(this.#floodTarget).abs().lessThan(0.1).and(seed.lessThan(0.92));
        If(floodActive.and(floodSelected), () => {
          const progress = clamp(floodAge.div(FLOOD_DURATION), 0, 1);
          const travel = smoothstep(0.02, 0.92, progress);
          const waveX = this.#frontline.add(this.#floodTarget.mul(travel.mul(4.25).add(0.25)));
          const band = mix(1, 0, smoothstep(0.08, 1.15, x.sub(waveX).abs()));
          const foam = sin(this.#time.mul(13).add(seed.mul(72))).abs();
          state.x.addAssign(info.x.mul(band).mul(0.82).mul(this.#motionScale));
          state.y.addAssign(band.mul(0.75).add(foam.mul(band).mul(0.82)).mul(this.#motionScale));
          state.z.addAssign(sin(seed.mul(117).add(this.#time.mul(5.2))).mul(band).mul(0.48).mul(this.#motionScale));
          state.w.addAssign(info.x.mul(band).mul(-0.72).mul(this.#motionScale));
        });

        const riftAge = this.#time.sub(this.#riftStart);
        const riftActive = riftAge.greaterThanEqual(0).and(riftAge.lessThan(RIFT_DURATION));
        const riftSelected = info.x.sub(this.#riftTarget).abs().lessThan(0.1).and(seed.lessThan(0.86));
        If(riftActive.and(riftSelected), () => {
          const progress = clamp(riftAge.div(RIFT_DURATION), 0, 1);
          const pulse = sin(progress.mul(PI));
          const centerX = this.#frontline.add(this.#riftTarget.mul(2.05));
          const proximity = smoothstep(2.6, 0.12, x.sub(centerX).abs());
          const fracture = proximity.mul(pulse).mul(this.#motionScale);
          state.x.addAssign(info.x.mul(fracture).mul(0.58));
          state.y.addAssign(fracture.mul(seed.mul(3.8).add(1.35)));
          state.z.addAssign(sin(seed.mul(131)).mul(fracture).mul(1.25));
          state.w.addAssign(sin(seed.mul(43)).mul(fracture).mul(2.1));
        });

        const catAge = this.#time.sub(this.#catStart);
        const catActive = catAge.greaterThanEqual(0).and(catAge.lessThan(CAT_RUSH_DURATION));
        const catSelected = info.x.sub(this.#catTarget).abs().lessThan(0.1).and(seed.lessThan(0.94));
        If(catActive.and(catSelected), () => {
          const progress = clamp(catAge.div(CAT_RUSH_DURATION), 0, 1);
          const travel = smoothstep(0.03, 0.88, progress);
          const catX = mix(this.#catTarget.mul(-5.35), this.#catTarget.mul(5.1), travel);
          const collision = mix(1, 0, smoothstep(0.05, 0.82, x.sub(catX).abs())).mul(this.#motionScale);
          state.x.addAssign(this.#catTarget.mul(collision).mul(1.05));
          state.y.addAssign(collision.mul(seed.mul(2.6).add(0.8)));
          state.z.addAssign(sin(seed.mul(137)).mul(collision).mul(1.7));
          state.w.addAssign(sin(seed.mul(53)).mul(collision).mul(2.4));
        });

        const bipedAge = this.#time.sub(this.#bipedStart);
        const bipedActive = bipedAge.greaterThanEqual(0).and(bipedAge.lessThan(BIPED_DURATION));
        const bipedSelected = info.x.sub(this.#bipedTarget).abs().lessThan(0.1).and(seed.lessThan(0.84));
        If(bipedActive.and(bipedSelected), () => {
          const progress = clamp(bipedAge.div(BIPED_DURATION), 0, 1);
          const travel = smoothstep(0.03, 0.92, progress);
          const mechX = mix(this.#bipedTarget.mul(-4.8), this.#bipedTarget.mul(3.85), travel);
          const band = mix(1, 0, smoothstep(0.08, 1.05, x.sub(mechX).abs()));
          const stomp = sin(bipedAge.mul(6.4)).abs().mul(band).mul(this.#motionScale);
          state.x.addAssign(this.#bipedTarget.mul(stomp).mul(0.62));
          state.y.addAssign(stomp.mul(seed.mul(1.8).add(0.55)));
          state.z.addAssign(sin(seed.mul(149)).mul(stomp).mul(0.74));
          state.w.addAssign(this.#bipedTarget.mul(stomp).mul(-0.9));
        });

        const houndAge = this.#time.sub(this.#houndStart);
        const houndActive = houndAge.greaterThanEqual(0).and(houndAge.lessThan(HOUND_DURATION));
        const houndSelected = info.x.sub(this.#houndTarget).abs().lessThan(0.1).and(seed.lessThan(0.9));
        If(houndActive.and(houndSelected), () => {
          const progress = clamp(houndAge.div(HOUND_DURATION), 0, 1);
          const travel = smoothstep(0.02, 0.9, progress);
          const houndX = mix(this.#houndTarget.mul(-5.15), this.#houndTarget.mul(4.65), travel);
          const collision = mix(1, 0, smoothstep(0.05, 0.92, x.sub(houndX).abs())).mul(this.#motionScale);
          state.x.addAssign(this.#houndTarget.mul(collision).mul(1.18));
          state.y.addAssign(collision.mul(seed.mul(2.1).add(0.68)));
          state.z.addAssign(sin(seed.mul(163)).mul(collision).mul(1.35));
          state.w.addAssign(sin(seed.mul(61)).mul(collision).mul(1.9));
        });
      });
    })().compute(GPU_MAX).setName("GPU Two-Team Horde + Cataclysm Physics");

    const renderState = states.toAttribute();
    const renderMeta = meta.toAttribute();
    const renderColor = colors.toAttribute();
    const scale = mix(0.62, 1.08, renderMeta.z).mul(0.78);
    const scaled = positionLocal.mul(scale);
    const rotation = renderState.w;
    const tilted = vec3(
      scaled.x.mul(cos(rotation)).sub(scaled.y.mul(sin(rotation))),
      scaled.x.mul(sin(rotation)).add(scaled.y.mul(cos(rotation))),
      scaled.z,
    );
    const worldPosition = tilted.add(renderState.xyz);
    const parts = battleParts();
    for (const { geometry, tone, palette, outline } of parts) {
      const material = new THREE.MeshToonNodeMaterial();
      material.gradientMap = this.#toonGradient;
      material.positionNode = worldPosition;
      const baseColor = palette === "fur"
        ? mix(renderColor.rgb, vec3(1, 0.86, 0.68), 0.42)
        : palette === "pillow"
          ? mix(renderColor.rgb, vec3(1, 0.94, 0.78), 0.74)
          : palette === "ink"
            ? renderColor.rgb.mul(0.2)
            : palette === "dark"
              ? renderColor.rgb.mul(0.48)
              : renderColor.rgb;
      material.colorNode = baseColor.mul(tone);
      if (outline) material.side = THREE.BackSide;
      const mesh = new THREE.InstancedMesh(geometry, material, GPU_MAX);
      const identity = new THREE.Matrix4();
      for (let index = 0; index < GPU_MAX; index += 1) mesh.setMatrixAt(index, identity);
      mesh.instanceMatrix.needsUpdate = true;
      mesh.count = GPU_MAX;
      mesh.frustumCulled = false;
      this.#meshes.push(mesh);
      scene.add(mesh);
    }
    this.#renderer.compute(this.#computeNode);
  }

  update(dt: number, time: number, frame: SwarmFrame): void {
    const ember = Math.min(GPU_PER_TEAM, Math.max(0, Math.floor(frame.emberVisible)));
    const aqua = Math.min(GPU_PER_TEAM, Math.max(0, Math.floor(frame.aquaVisible)));
    this.#visible = [ember, aqua];
    this.#time.value = time;
    this.#delta.value = Math.min(0.05, dt);
    this.#frontline.value = frame.frontline;
    this.#emberCount.value = ember;
    this.#aquaCount.value = aqua;
    this.#motionScale.value = frame.reducedMotion ? 0.25 : 1;
    this.#renderer.compute(this.#computeNode);
  }

  triggerCharge(team: BattleTeam, time: number): void {
    if (team === "ember") this.#emberChargeStart.value = time;
    else this.#aquaChargeStart.value = time;
  }

  triggerTornado(team: BattleTeam, time: number): void {
    this.#tornadoTarget.value = team === "ember" ? -1 : 1;
    this.#tornadoStart.value = time;
  }

  triggerFlood(team: BattleTeam, time: number): void {
    this.#floodTarget.value = team === "ember" ? -1 : 1;
    this.#floodStart.value = time;
  }

  triggerRift(team: BattleTeam, time: number): void {
    this.#riftTarget.value = team === "ember" ? -1 : 1;
    this.#riftStart.value = time;
  }

  triggerCatRush(attacker: BattleTeam, time: number): void {
    this.#catTarget.value = attacker === "ember" ? 1 : -1;
    this.#catStart.value = time;
  }

  triggerBiped(attacker: BattleTeam, time: number): void {
    this.#bipedTarget.value = attacker === "ember" ? 1 : -1;
    this.#bipedStart.value = time;
  }

  triggerHound(attacker: BattleTeam, time: number): void {
    this.#houndTarget.value = attacker === "ember" ? 1 : -1;
    this.#houndStart.value = time;
  }

  snapshot(): { emberVisible: number; aquaVisible: number; capacity: number } {
    return { emberVisible: this.#visible[0], aquaVisible: this.#visible[1], capacity: GPU_MAX };
  }

  dispose(): void {
    for (const mesh of this.#meshes) {
      mesh.removeFromParent();
      mesh.geometry.dispose();
      (mesh.material as THREE.Material).dispose();
    }
    this.#toonGradient.dispose();
  }

  #initializeAttributes(): void {
    const meta = this.#metaAttribute.array as Float32Array;
    const state = this.#stateAttribute.array as Float32Array;
    const color = this.#colorAttribute.array as Float32Array;
    for (let index = 0; index < GPU_MAX; index += 1) {
      const team = index < GPU_PER_TEAM ? -1 : 1;
      const localIndex = index % GPU_PER_TEAM;
      const seed = scatter01(index + 11, 0);
      const rank = scatter01(index + 11, 1);
      meta[index * 4] = team;
      meta[index * 4 + 1] = localIndex;
      meta[index * 4 + 2] = seed;
      meta[index * 4 + 3] = rank;
      state[index * 4] = team * 5.3;
      state[index * 4 + 1] = 0;
      state[index * 4 + 2] = (seed * 2 - 1) * 2.6;
      state[index * 4 + 3] = 0;
      const tint = paletteColor(team, index);
      color[index * 4] = tint.r;
      color[index * 4 + 1] = tint.g;
      color[index * 4 + 2] = tint.b;
      color[index * 4 + 3] = 1;
    }
    this.#metaAttribute.needsUpdate = true;
    this.#stateAttribute.needsUpdate = true;
    this.#colorAttribute.needsUpdate = true;
  }
}

export class CpuBattleSwarm implements BattleSwarm {
  readonly gpuSimulation = false;
  readonly #meshes: THREE.InstancedMesh[] = [];
  readonly #toonGradient = createToonGradient();
  #visible: [number, number] = [0, 0];
  #emberChargeStart = -10;
  #aquaChargeStart = -10;
  #stormTeam: BattleTeam = "ember";
  #stormStart = -10;
  #floodTeam: BattleTeam = "ember";
  #floodStart = -10;
  #riftTeam: BattleTeam = "ember";
  #riftStart = -10;
  #catTarget: BattleTeam = "aqua";
  #catStart = -10;
  #bipedTarget: BattleTeam = "aqua";
  #bipedStart = -10;
  #houndTarget: BattleTeam = "aqua";
  #houndStart = -10;

  constructor(scene: THREE.Scene) {
    for (const { geometry, tone, palette, outline } of battleParts()) {
      const material = new THREE.MeshToonMaterial({ color: 0xffffff, vertexColors: true, gradientMap: this.#toonGradient });
      material.userData.tone = tone;
      material.userData.palette = palette;
      if (outline) material.side = THREE.BackSide;
      const mesh = new THREE.InstancedMesh(geometry, material, CPU_MAX);
      mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
      mesh.frustumCulled = false;
      mesh.count = 0;
      this.#meshes.push(mesh);
      scene.add(mesh);
    }
  }

  update(_dt: number, time: number, frame: SwarmFrame): void {
    const counts: [number, number] = [
      Math.min(CPU_PER_TEAM, Math.max(0, Math.floor(frame.emberVisible / 4))),
      Math.min(CPU_PER_TEAM, Math.max(0, Math.floor(frame.aquaVisible / 4))),
    ];
    this.#visible = [counts[0] * 4, counts[1] * 4];
    let slot = 0;
    for (let teamIndex = 0; teamIndex < 2; teamIndex += 1) {
      const teamSign = teamIndex === 0 ? -1 : 1;
      for (let index = 0; index < counts[teamIndex]!; index += 1) {
        const seed = scatter01(index + teamIndex * 1009, 0);
        const rank = scatter01(index + teamIndex * 1009, 1);
        const depth = 0.16 + seed * 2.65 + rank * 1.25;
        const chargeStart = teamSign < 0 ? this.#emberChargeStart : this.#aquaChargeStart;
        const chargeAge = time - chargeStart;
        const chargeEnvelope = chargeAge >= 0 && chargeAge < 0.78 ? Math.sin(chargeAge / 0.78 * Math.PI) : 0;
        const collisionDepth = Math.max(0.1, depth - chargeEnvelope * THREE.MathUtils.lerp(0.48, 0.18, rank));
        const engaged = 1 - THREE.MathUtils.smoothstep(collisionDepth, 0.12, 1.28);
        const recoil = Math.abs(Math.sin(time * 17 + seed * 83 + rank * 29)) * 0.16 * engaged;
        let x = frame.frontline + teamSign * (collisionDepth + recoil);
        let y = frame.reducedMotion ? 0 : Math.abs(Math.sin(time * 9.5 + seed * 52)) * 0.075 + chargeEnvelope * 0.1;
        let z = Math.sin(seed * 91.7) * 2.65 + Math.sin(rank * 37.1) * 0.38;
        let tilt = Math.sin(time * 11 + seed * 19) * 0.13 * engaged - teamSign * chargeEnvelope * 0.13;
        const stormAge = time - this.#stormStart;
        const targetSign = this.#stormTeam === "ember" ? -1 : 1;
        if (teamSign === targetSign && seed < 0.78 && stormAge >= 0 && stormAge < TORNADO_DURATION) {
          const progress = THREE.MathUtils.clamp(stormAge / TORNADO_DURATION, 0, 1);
          const suction = THREE.MathUtils.smoothstep(progress, 0.02, 0.62);
          const release = progress <= 0.93 ? 1 : 0;
          const angle = seed * 57 + progress * Math.PI * 12;
          const radius = THREE.MathUtils.lerp(3.25, 0.22, suction);
          x = THREE.MathUtils.lerp(x, frame.frontline + targetSign * 1.55 + Math.cos(angle) * radius, suction * release);
          z = THREE.MathUtils.lerp(z, Math.sin(angle) * radius, suction * release);
          y += (Math.sin(progress * Math.PI) * 2.4 + suction * (seed * 5.8 + 1.1)) * release;
          tilt = angle;
        }
        const floodAge = time - this.#floodStart;
        const floodTargetSign = this.#floodTeam === "ember" ? -1 : 1;
        if (teamSign === floodTargetSign && seed < 0.92 && floodAge >= 0 && floodAge < FLOOD_DURATION) {
          const progress = THREE.MathUtils.clamp(floodAge / FLOOD_DURATION, 0, 1);
          const travel = THREE.MathUtils.smoothstep(progress, 0.02, 0.92);
          const waveX = frame.frontline + floodTargetSign * (0.25 + travel * 4.25);
          const band = 1 - THREE.MathUtils.smoothstep(Math.abs(x - waveX), 0.08, 1.15);
          const foam = Math.abs(Math.sin(time * 13 + seed * 72));
          x += teamSign * band * 0.82;
          y += band * 0.75 + foam * band * 0.82;
          z += Math.sin(seed * 117 + time * 5.2) * band * 0.48;
          tilt -= teamSign * band * 0.72;
        }
        const riftAge = time - this.#riftStart;
        const riftTargetSign = this.#riftTeam === "ember" ? -1 : 1;
        if (teamSign === riftTargetSign && seed < 0.86 && riftAge >= 0 && riftAge < RIFT_DURATION) {
          const progress = THREE.MathUtils.clamp(riftAge / RIFT_DURATION, 0, 1);
          const pulse = Math.sin(progress * Math.PI);
          const centerX = frame.frontline + riftTargetSign * 2.05;
          const proximity = 1 - THREE.MathUtils.smoothstep(Math.abs(x - centerX), 0.12, 2.6);
          const fracture = proximity * pulse;
          x += teamSign * fracture * 0.58;
          y += fracture * (seed * 3.8 + 1.35);
          z += Math.sin(seed * 131) * fracture * 1.25;
          tilt += Math.sin(seed * 43) * fracture * 2.1;
        }
        const catAge = time - this.#catStart;
        const catTargetSign = this.#catTarget === "ember" ? -1 : 1;
        if (teamSign === catTargetSign && seed < 0.94 && catAge >= 0 && catAge < CAT_RUSH_DURATION) {
          const progress = THREE.MathUtils.clamp(catAge / CAT_RUSH_DURATION, 0, 1);
          const travel = THREE.MathUtils.smoothstep(progress, 0.03, 0.88);
          const catX = THREE.MathUtils.lerp(-catTargetSign * 5.35, catTargetSign * 5.1, travel);
          const collision = 1 - THREE.MathUtils.smoothstep(Math.abs(x - catX), 0.05, 0.82);
          x += catTargetSign * collision * 1.05;
          y += collision * (seed * 2.6 + 0.8);
          z += Math.sin(seed * 137) * collision * 1.7;
          tilt += Math.sin(seed * 53) * collision * 2.4;
        }
        const bipedAge = time - this.#bipedStart;
        const bipedTargetSign = this.#bipedTarget === "ember" ? -1 : 1;
        if (teamSign === bipedTargetSign && seed < 0.84 && bipedAge >= 0 && bipedAge < BIPED_DURATION) {
          const progress = THREE.MathUtils.clamp(bipedAge / BIPED_DURATION, 0, 1);
          const travel = THREE.MathUtils.smoothstep(progress, 0.03, 0.92);
          const mechX = THREE.MathUtils.lerp(-bipedTargetSign * 4.8, bipedTargetSign * 3.85, travel);
          const band = 1 - THREE.MathUtils.smoothstep(Math.abs(x - mechX), 0.08, 1.05);
          const stomp = Math.abs(Math.sin(bipedAge * 6.4)) * band;
          x += bipedTargetSign * stomp * 0.62;
          y += stomp * (seed * 1.8 + 0.55);
          z += Math.sin(seed * 149) * stomp * 0.74;
          tilt -= bipedTargetSign * stomp * 0.9;
        }
        const houndAge = time - this.#houndStart;
        const houndTargetSign = this.#houndTarget === "ember" ? -1 : 1;
        if (teamSign === houndTargetSign && seed < 0.9 && houndAge >= 0 && houndAge < HOUND_DURATION) {
          const progress = THREE.MathUtils.clamp(houndAge / HOUND_DURATION, 0, 1);
          const travel = THREE.MathUtils.smoothstep(progress, 0.02, 0.9);
          const houndX = THREE.MathUtils.lerp(-houndTargetSign * 5.15, houndTargetSign * 4.65, travel);
          const collision = 1 - THREE.MathUtils.smoothstep(Math.abs(x - houndX), 0.05, 0.92);
          x += houndTargetSign * collision * 1.18;
          y += collision * (seed * 2.1 + 0.68);
          z += Math.sin(seed * 163) * collision * 1.35;
          tilt += Math.sin(seed * 61) * collision * 1.9;
        }
        EULER.set(0, 0, tilt);
        QUATERNION.setFromEuler(EULER);
        const scale = THREE.MathUtils.lerp(0.62, 1.08, seed) * 0.78;
        POSITION.set(x, y, z);
        SCALE.setScalar(scale);
        MATRIX.compose(POSITION, QUATERNION, SCALE);
        const tint = paletteColor(teamSign, index);
        for (const mesh of this.#meshes) {
          mesh.setMatrixAt(slot, MATRIX);
          const material = mesh.material as THREE.Material;
          const tone = Number(material.userData.tone ?? 1);
          mesh.setColorAt(slot, partColor(TINT, tint, String(material.userData.palette), tone));
        }
        slot += 1;
      }
    }
    for (const mesh of this.#meshes) {
      mesh.count = slot;
      mesh.instanceMatrix.needsUpdate = true;
      if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
    }
  }

  triggerCharge(team: BattleTeam, time: number): void {
    if (team === "ember") this.#emberChargeStart = time;
    else this.#aquaChargeStart = time;
  }

  triggerTornado(team: BattleTeam, time: number): void {
    this.#stormTeam = team;
    this.#stormStart = time;
  }

  triggerFlood(team: BattleTeam, time: number): void {
    this.#floodTeam = team;
    this.#floodStart = time;
  }

  triggerRift(team: BattleTeam, time: number): void {
    this.#riftTeam = team;
    this.#riftStart = time;
  }

  triggerCatRush(attacker: BattleTeam, time: number): void {
    this.#catTarget = attacker === "ember" ? "aqua" : "ember";
    this.#catStart = time;
  }

  triggerBiped(attacker: BattleTeam, time: number): void {
    this.#bipedTarget = attacker === "ember" ? "aqua" : "ember";
    this.#bipedStart = time;
  }

  triggerHound(attacker: BattleTeam, time: number): void {
    this.#houndTarget = attacker === "ember" ? "aqua" : "ember";
    this.#houndStart = time;
  }

  snapshot(): { emberVisible: number; aquaVisible: number; capacity: number } {
    return { emberVisible: this.#visible[0], aquaVisible: this.#visible[1], capacity: CPU_MAX * 4 };
  }

  dispose(): void {
    for (const mesh of this.#meshes) {
      mesh.removeFromParent();
      mesh.geometry.dispose();
      (mesh.material as THREE.Material).dispose();
    }
    this.#toonGradient.dispose();
  }
}

type PartPalette = "team" | "fur" | "dark" | "pillow" | "ink";

function battleParts(): Array<{ geometry: THREE.BufferGeometry; tone: number; palette: PartPalette; outline?: boolean }> {
  return [
    { geometry: new THREE.CapsuleGeometry(0.132, 0.19, 3, 5).translate(0, 0.24, 0), tone: 1, palette: "ink", outline: true },
    { geometry: new THREE.IcosahedronGeometry(0.187, 0).scale(1.06, 0.91, 1).translate(0, 0.54, 0), tone: 1, palette: "ink", outline: true },
    { geometry: new THREE.CapsuleGeometry(0.12, 0.18, 3, 5).translate(0, 0.24, 0), tone: 0.88, palette: "team" },
    { geometry: new THREE.IcosahedronGeometry(0.17, 0).scale(1.06, 0.91, 1).translate(0, 0.54, 0), tone: 1, palette: "fur" },
    { geometry: new THREE.ConeGeometry(0.066, 0.16, 5).rotateZ(0.26).translate(-0.1, 0.7, 0), tone: 0.9, palette: "dark" },
    { geometry: new THREE.ConeGeometry(0.066, 0.16, 5).rotateZ(-0.26).translate(0.1, 0.7, 0), tone: 0.9, palette: "dark" },
    { geometry: new THREE.IcosahedronGeometry(0.15, 0).scale(1.18, 0.78, 0.34).translate(0, 0.33, -0.13), tone: 1, palette: "pillow" },
    { geometry: new THREE.BoxGeometry(0.15, 0.035, 0.025).translate(0, 0.575, -0.17), tone: 0.95, palette: "ink" },
  ];
}

function partColor(target: THREE.Color, tint: THREE.Color, palette: string, tone: number): THREE.Color {
  target.copy(tint);
  if (palette === "fur") target.lerp(FUR, 0.42);
  else if (palette === "pillow") target.lerp(PILLOW, 0.74);
  else if (palette === "ink") target.multiplyScalar(0.2);
  else if (palette === "dark") target.multiplyScalar(0.48);
  return target.multiplyScalar(tone);
}

function paletteColor(team: number, index: number): THREE.Color {
  const ember = [0xff5f4f, 0xea8b3f, 0xeb4678, 0xf5b82f];
  const aqua = [0x35c9c2, 0x3e91e8, 0x755fe2, 0x50bb70];
  const palette = team < 0 ? ember : aqua;
  return new THREE.Color(palette[index % palette.length]!);
}

function scatter01(seed: number, channel: number): number {
  let value = seed ^ Math.imul(channel + 1, 0x9e3779b1);
  value = Math.imul(value ^ (value >>> 16), 0x21f0aaad);
  value = Math.imul(value ^ (value >>> 15), 0x735a2d97);
  return ((value ^ (value >>> 15)) >>> 0) / 0xffffffff;
}
